import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, User, Building2, Phone, Mail, Calendar, UserPlus } from 'lucide-react';

export default function EmployeeManagement() {
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_code: '',
    name: '',
    designation: '',
    department: '',
    phone: '',
    email: '',
    address: '',
    joining_date: '',
    hotel_id: ''
  });
  
  // Assign user dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  const isCompanyAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const isHotelAdmin = user?.role === 'hotel_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesRes, hotelsRes] = await Promise.all([
        api.get('/employees'),
        isCompanyAdmin ? api.get('/companies/my-hotels').catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setEmployees(employeesRes.data);
      setHotels(hotelsRes.data || []);
      
      // Load users for assignment (hotel admin only)
      if (isHotelAdmin) {
        const usersRes = await api.get('/auth/users').catch(() => ({ data: [] }));
        setUsers(usersRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, formData);
        toast.success('Employee updated');
      } else {
        await api.post('/employees', formData);
        toast.success('Employee created');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this employee?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await api.put(`/employees/${employee.id}`, { is_active: !employee.is_active });
      toast.success(`Employee ${employee.is_active ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update employee');
    }
  };

  const openEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_code: employee.employee_code || '',
      name: employee.name || '',
      designation: employee.designation || '',
      department: employee.department || '',
      phone: employee.phone || '',
      email: employee.email || '',
      address: employee.address || '',
      joining_date: employee.joining_date || '',
      hotel_id: employee.hotel_id || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      employee_code: '',
      name: '',
      designation: '',
      department: '',
      phone: '',
      email: '',
      address: '',
      joining_date: '',
      hotel_id: user?.hotel_id || ''
    });
  };

  const openAssignDialog = async (employee) => {
    setSelectedEmployee(employee);
    setSelectedUserId(employee.assigned_user_id || '');
    
    // Load users for this hotel
    try {
      const usersRes = await api.get('/auth/users');
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    
    setAssignDialogOpen(true);
  };

  const handleAssignUser = async () => {
    if (!selectedEmployee) return;
    try {
      await api.post(`/employees/${selectedEmployee.id}/assign-user`, {
        user_id: selectedUserId || null
      });
      toast.success(selectedUserId ? 'User assigned to employee' : 'User unassigned');
      setAssignDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to assign user');
    }
  };

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel?.name || hotelId || 'Not Assigned';
  };

  if (loading) {
    return <div className="text-center py-12">Loading employees...</div>;
  }

  return (
    <div className="space-y-8" data-testid="employee-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {isCompanyAdmin ? 'Employee Management' : 'Staff Directory'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isCompanyAdmin ? 'Manage employees across your hotels' : 'View and assign users to employees'}
          </p>
        </div>
        {isCompanyAdmin && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-employee-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Employee
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employees ({employees.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Department</TableHead>
                {isCompanyAdmin && <TableHead>Hotel</TableHead>}
                <TableHead>Contact</TableHead>
                <TableHead>Assigned User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-medium">{employee.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{employee.employee_code}</TableCell>
                  <TableCell>{employee.designation || '-'}</TableCell>
                  <TableCell>{employee.department || '-'}</TableCell>
                  {isCompanyAdmin && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-gray-400" />
                        <span className="text-sm">{getHotelName(employee.hotel_id)}</span>
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="text-sm">
                      {employee.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-gray-400" />
                          {employee.phone}
                        </div>
                      )}
                      {employee.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-gray-400" />
                          {employee.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {employee.assigned_user_email ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {employee.assigned_user_email}
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">Not assigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={employee.is_active !== false ? 'default' : 'outline'} className={employee.is_active !== false ? 'bg-green-500' : ''}>
                      {employee.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {(isHotelAdmin || isCompanyAdmin) && (
                        <Button variant="ghost" size="icon" onClick={() => openAssignDialog(employee)} title="Assign User">
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      )}
                      {isCompanyAdmin && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(employee)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleToggleActive(employee)}>
                            <Switch checked={employee.is_active !== false} className="scale-75" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(employee.id)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {employees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isCompanyAdmin ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    No employees found. {isCompanyAdmin && 'Click "Add Employee" to create one.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Employee Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>Employee details for your organization</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee Code *</Label>
                <Input 
                  value={formData.employee_code} 
                  onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })} 
                  placeholder="EMP001"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  placeholder="Full Name"
                  required 
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input 
                  value={formData.designation} 
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })} 
                  placeholder="e.g., Front Desk Manager"
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={formData.department || 'none'} onValueChange={(v) => setFormData({ ...formData, department: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    <SelectItem value="Front Office">Front Office</SelectItem>
                    <SelectItem value="Housekeeping">Housekeeping</SelectItem>
                    <SelectItem value="F&B Service">F&B Service</SelectItem>
                    <SelectItem value="Kitchen">Kitchen</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                    <SelectItem value="Accounts">Accounts</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isCompanyAdmin && hotels.length > 0 && (
              <div className="space-y-2">
                <Label>Assign to Hotel</Label>
                <Select value={formData.hotel_id || 'none'} onValueChange={(v) => setFormData({ ...formData, hotel_id: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Hotel</SelectItem>
                    {hotels.map((hotel) => (
                      <SelectItem key={hotel.id} value={hotel.id}>{hotel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                  placeholder="+91 9876543210"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  placeholder="employee@hotel.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input 
                value={formData.address} 
                onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                placeholder="Full address"
              />
            </div>

            <div className="space-y-2">
              <Label>Joining Date</Label>
              <Input 
                type="date"
                value={formData.joining_date} 
                onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} 
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingEmployee ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign User to Employee</DialogTitle>
            <DialogDescription>
              Link a system user account to {selectedEmployee?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={selectedUserId || 'none'} onValueChange={(v) => setSelectedUserId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No User (Unassign)</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignUser}>
                {selectedUserId ? 'Assign User' : 'Unassign'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
